import React from 'react';
import { colors, type, radius, btn, input as inputStyle, label as labelStyle, dialog as dialogStyle, brand } from '../theme';

// ─── Logo ───
// The cloud mark from the brand kit, inlined so it sits on any surface without
// the kit's Abyss background rect. Layer order per the kit: base, Tide puff,
// Sand puff, red dot. The wordmark is live text in Inter (the kit's outlined
// paths are Inter Display Black), uppercased by CSS so the DOM still reads
// "Mayday Cloud" for assistive tech and tests.
export function CloudMark({ size = 24, style, ...rest }) {
  // Content box of the kit's 160x160 mark after its own translate(0,-10).
  const h = Math.round(size * 98 / 136);
  return (
    <svg
      width={size}
      height={h}
      viewBox="12 32 136 98"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block', flexShrink: 0, ...style }}
      {...rest}
    >
      <g transform="translate(0,-10)">
        <rect x="12" y="78" width="136" height="62" rx="31" fill={brand.foam} />
        <circle cx="62" cy="76" r="34" fill={brand.tide} />
        <circle cx="104" cy="86" r="24" fill={brand.sand} />
        <circle cx="116" cy="117" r="11" fill={brand.brick} />
      </g>
    </svg>
  );
}

export function Logo({ variant = 'horizontal', size = 24, as = 'div', style }) {
  const Tag = as;
  const stacked = variant === 'stacked';
  const mark = variant === 'mark';
  if (mark) return <CloudMark size={size} style={style} />;

  const mayday = {
    display: 'block',
    fontWeight: 800,
    color: colors.text,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    lineHeight: 1,
    fontSize: stacked ? `${Math.round(size * 0.42)}px` : `${Math.round(size * 0.5)}px`,
  };
  const cloud = {
    display: 'block',
    fontWeight: 600,
    color: brand.sand,
    letterSpacing: '0.32em',
    textTransform: 'uppercase',
    lineHeight: 1,
    marginTop: stacked ? `${Math.round(size * 0.12)}px` : `${Math.round(size * 0.14)}px`,
    fontSize: stacked ? `${Math.round(size * 0.2)}px` : `${Math.round(size * 0.3)}px`,
    // Optically centers the tracked word under MAYDAY.
    paddingLeft: '0.32em',
  };

  return (
    <Tag
      aria-label="Mayday Cloud"
      style={{
        display: 'flex',
        flexDirection: stacked ? 'column' : 'row',
        alignItems: 'center',
        gap: stacked ? `${Math.round(size * 0.28)}px` : `${Math.round(size * 0.4)}px`,
        margin: 0,
        ...style,
      }}
    >
      <CloudMark size={size} />
      <span style={{ display: 'block', textAlign: stacked ? 'center' : 'left' }}>
        <span style={mayday}>Mayday</span>{' '}
        <span style={cloud}>Cloud</span>
      </span>
    </Tag>
  );
}

// ─── Button ───
// variant: primary (Tide) | sync (Brick, uploads only) | ghost | subtle | danger
export function Button({ variant = 'ghost', size, block, icon, children, style, className, ...rest }) {
  const filled = variant === 'primary' || variant === 'sync';
  const cls = [filled ? 'mc-btn' : 'mc-ghost', className].filter(Boolean).join(' ');
  return (
    <button
      className={cls}
      style={{
        ...btn.base,
        ...(btn[variant] || {}),
        ...(size === 'sm' ? btn.sm : size === 'lg' ? btn.lg : {}),
        ...(block ? btn.block : {}),
        ...(icon && !children ? (size === 'sm' ? btn.iconSm : btn.icon) : {}),
        ...style,
      }}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}

// ─── Inputs ───
export function Input({ style, ...rest }) {
  return <input style={{ ...inputStyle, ...style }} {...rest} />;
}

export function Field({ label, hint, children, style }) {
  return (
    <label style={{ display: 'block', marginBottom: '14px', ...style }}>
      {label && <span style={{ ...labelStyle, display: 'block', marginBottom: '6px' }}>{label}</span>}
      {children}
      {hint && <span style={{ display: 'block', fontSize: type.sm, color: colors.textMuted, marginTop: '6px' }}>{hint}</span>}
    </label>
  );
}

export function SectionLabel({ children, style }) {
  return <div style={{ ...labelStyle, ...style }}>{children}</div>;
}

// ─── Dialog ───
export function Dialog({ title, subtitle, onClose, children, width, style }) {
  return (
    <div style={dialogStyle.overlay} onClick={onClose}>
      <div
        className="mc-fade-in"
        role="dialog"
        aria-modal="true"
        style={{ ...dialogStyle.card, ...(width ? { maxWidth: width } : {}), ...style }}
        onClick={e => e.stopPropagation()}
      >
        {title && <div style={dialogStyle.title}>{title}</div>}
        {subtitle && <div style={dialogStyle.subtitle}>{subtitle}</div>}
        {children}
      </div>
    </div>
  );
}

export function DialogActions({ children }) {
  return <div style={dialogStyle.actions}>{children}</div>;
}

// ─── Empty state ───
export function EmptyState({ icon, title, hint, style }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '72px 0', textAlign: 'center', ...style }}>
      {icon && <div style={{ color: colors.textFaint, marginBottom: '12px' }}>{icon}</div>}
      {title && <div style={{ fontSize: type.base, color: colors.textSecondary }}>{title}</div>}
      {hint && <div style={{ fontSize: type.md, color: colors.textMuted, marginTop: '4px' }}>{hint}</div>}
    </div>
  );
}

// ─── Notice ───
// tone: danger | success | warning | info
export function Notice({ tone = 'info', children, style }) {
  const tones = {
    danger: { background: colors.dangerSoft, border: `1px solid ${colors.dangerBorder}`, color: colors.danger },
    success: { background: colors.accentSoft, border: `1px solid ${colors.accentBorder}`, color: colors.accentText },
    warning: { background: colors.sandSoft, border: `1px solid ${colors.sandBorder}`, color: colors.sand },
    info: { background: colors.hover, border: `1px solid ${colors.border}`, color: colors.textSecondary },
  };
  return (
    <div style={{ padding: '10px 12px', borderRadius: radius.md, fontSize: type.md, lineHeight: 1.4, ...tones[tone], ...style }}>
      {children}
    </div>
  );
}

// ─── Auth shell ───
// Centered card used by login, reset, drop, loading and error screens.
export function CenteredCard({ children, width = '400px', style }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '24px 16px', background: colors.bg }}>
      <div
        className="mc-fade-in"
        style={{
          width: '100%',
          maxWidth: width,
          background: colors.panel,
          border: `1px solid ${colors.border}`,
          borderRadius: radius.xl,
          padding: '32px 28px',
          ...style,
        }}
      >
        {children}
      </div>
    </div>
  );
}
